// LLM:METADATA
// :topology: [Domain:Observability | Role:EventRegistry | Pattern:Registry]
// :relates-to: required-by: "All Components", uses: "logger.js"
// :rationale: "Centralized event ID registry ensures consistent semantic events for automated verification"
// :contract: pre: "Registry initialized", post: "Event IDs defined", invariant: "All event IDs are unique"
// :decision_cache: "Chose centralized registry over distributed IDs for consistency [OBS-004]"
// LLM:END

/**
 * Event ID Registry
 * Defines all semantic event IDs for logging and automated verification
 */

// ==================== Contract Validation Events ====================
export const CONTRACT_VIOLATION = "CONTRACT_VIOLATION"
export const CONTRACT_VALIDATION_SUCCESS = "CONTRACT_VALIDATION_SUCCESS"

// ==================== Logging Events ====================
export const LOG_EMIT_START = "LOG_EMIT_START"
export const LOG_EMIT_COMPLETE = "LOG_EMIT_COMPLETE"
export const LOG_EMIT_FAIL = "LOG_EMIT_FAIL"
export const LOG_FILTER_START = "LOG_FILTER_START"
export const LOG_FILTER_COMPLETE = "LOG_FILTER_COMPLETE"

// ==================== Authentication Events ====================
export const AUTH_TOKEN_STORE_START = "AUTH_TOKEN_STORE_START"
export const AUTH_TOKEN_STORE_COMPLETE = "AUTH_TOKEN_STORE_COMPLETE"
export const AUTH_TOKEN_STORE_FAIL = "AUTH_TOKEN_STORE_FAIL"
export const AUTH_TOKEN_STORED = "AUTH_TOKEN_STORED"
export const AUTH_TOKEN_ENCRYPTED = "AUTH_TOKEN_ENCRYPTED"
export const AUTH_TOKEN_CLEARED = "AUTH_TOKEN_CLEARED"
export const AUTH_TOKEN_VALIDATE_START = "AUTH_TOKEN_VALIDATE_START"
export const AUTH_TOKEN_VALIDATE_COMPLETE = "AUTH_TOKEN_VALIDATE_COMPLETE"
export const AUTH_TOKEN_VALIDATE_FAIL = "AUTH_TOKEN_VALIDATE_FAIL"
export const AUTH_TOKEN_REFRESH_START = "AUTH_TOKEN_REFRESH_START"
export const AUTH_TOKEN_REFRESH_COMPLETE = "AUTH_TOKEN_REFRESH_COMPLETE"
export const AUTH_TOKEN_REFRESH_FAIL = "AUTH_TOKEN_REFRESH_FAIL"
export const AUTH_LOGIN_START = "LoginInit"
export const AUTH_LOGIN_COMPLETE = "LoginSuccess"
export const AUTH_LOGIN_FAIL = "AUTH_LOGIN_FAIL"
export const AUTH_LOGOUT_START = "LogoutInit"
export const AUTH_LOGOUT_COMPLETE = "LogoutSuccess"
export const AUTH_LOGOUT_FAIL = "AUTH_LOGOUT_FAIL"
export const AUTH_CHECK_START = "AUTH_CHECK_START"
export const AUTH_CHECK_COMPLETE = "AUTH_CHECK_COMPLETE"
export const AUTH_CHECK_FAIL = "AUTH_CHECK_FAIL"
export const AUTH_STATUS_TRANSITION = "AUTH_STATUS_TRANSITION"

// ==================== CSRF Events ====================
export const CSRF_TOKEN_FETCH_START = "CSRF_TOKEN_FETCH_START"
export const CSRF_TOKEN_FETCH_COMPLETE = "CSRF_TOKEN_FETCH_COMPLETE"
export const CSRF_TOKEN_FETCH_FAIL = "CSRF_TOKEN_FETCH_FAIL"
export const CSRF_TOKEN_GENERATED = "CSRF_TOKEN_GENERATED"
export const CSRF_TOKEN_MISSING = "CSRF_TOKEN_MISSING"
export const CSRF_REQUEST_REJECTED = "CSRF_REQUEST_REJECTED"
export const CSRF_TOKEN_VALIDATE_START = "CSRF_TOKEN_VALIDATE_START"
export const CSRF_TOKEN_VALIDATE_COMPLETE = "CSRF_TOKEN_VALIDATE_COMPLETE"
export const CSRF_TOKEN_VALIDATE_FAIL = "CSRF_TOKEN_VALIDATE_FAIL"

// ==================== API Events ====================
export const API_REQUEST_START = "API_REQUEST_START"
export const API_REQUEST_COMPLETE = "API_REQUEST_COMPLETE"
export const API_REQUEST_FAIL = "API_REQUEST_FAIL"
export const API_RESPONSE_START = "API_RESPONSE_START"
export const API_RESPONSE_COMPLETE = "API_RESPONSE_COMPLETE"
export const API_RESPONSE_FAIL = "API_RESPONSE_FAIL"
export const API_INTERCEPTOR_REQUEST = "API_INTERCEPTOR_REQUEST"
export const API_INTERCEPTOR_RESPONSE = "API_INTERCEPTOR_RESPONSE"
export const API_INTERCEPTOR_ERROR = "API_INTERCEPTOR_ERROR"

// ==================== Error Handling Events ====================
export const ERROR_HANDLE_START = "ERROR_HANDLE_START"
export const ERROR_HANDLE_COMPLETE = "ERROR_HANDLE_COMPLETE"
export const ERROR_HANDLE_FAIL = "ERROR_HANDLE_FAIL"
export const ERROR_NOTIFY_START = "ERROR_NOTIFY_START"
export const ERROR_NOTIFY_COMPLETE = "ERROR_NOTIFY_COMPLETE"

// ==================== Module System Events ====================
export const MODULE_ITEMS_LOAD_START = "MODULE_ITEMS_LOAD_START"
export const MODULE_ITEMS_LOAD_COMPLETE = "MODULE_ITEMS_LOAD_COMPLETE"
export const MODULE_ITEMS_LOAD_FAIL = "MODULE_ITEMS_LOAD_FAIL"
export const MODULE_ITEMS_FILTER_START = "MODULE_ITEMS_FILTER_START"
export const MODULE_ITEMS_FILTER_COMPLETE = "MODULE_ITEMS_FILTER_COMPLETE"
export const MODULE_ITEMS_FILTER_FAIL = "MODULE_ITEMS_FILTER_FAIL"
export const MODULE_ITEMS_FILTER_CHANGED = "MODULE_ITEMS_FILTER_CHANGED"
export const MODULE_ITEMS_SORT_START = "MODULE_ITEMS_SORT_START"
export const MODULE_ITEMS_SORT_COMPLETE = "MODULE_ITEMS_SORT_COMPLETE"
export const MODULE_ITEMS_SORT_FAIL = "MODULE_ITEMS_SORT_FAIL"
export const MODULE_ITEMS_PAGINATE_START = "MODULE_ITEMS_PAGINATE_START"
export const MODULE_ITEMS_PAGINATE_COMPLETE = "MODULE_ITEMS_PAGINATE_COMPLETE"
export const MODULE_ITEMS_PAGINATE_FAIL = "MODULE_ITEMS_PAGINATE_FAIL"

export const MODULE_EDIT_START = "MODULE_EDIT_START"
export const MODULE_EDIT_COMPLETE = "MODULE_EDIT_COMPLETE"
export const MODULE_EDIT_FAIL = "MODULE_EDIT_FAIL"
export const MODULE_EDIT_VALIDATE_START = "MODULE_EDIT_VALIDATE_START"
export const MODULE_EDIT_VALIDATE_COMPLETE = "MODULE_EDIT_VALIDATE_COMPLETE"
export const MODULE_EDIT_VALIDATE_FAIL = "MODULE_EDIT_VALIDATE_FAIL"
export const MODULE_EDIT_SUBMIT_START = "MODULE_EDIT_SUBMIT_START"
export const MODULE_EDIT_SUBMIT_COMPLETE = "MODULE_EDIT_SUBMIT_COMPLETE"
export const MODULE_EDIT_SUBMIT_FAIL = "MODULE_EDIT_SUBMIT_FAIL"

export const MODULE_NEW_ITEM_CREATE_START = "MODULE_NEW_ITEM_CREATE_START"
export const MODULE_NEW_ITEM_CREATE_COMPLETE = "MODULE_NEW_ITEM_CREATE_COMPLETE"
export const MODULE_NEW_ITEM_CREATE_FAIL = "MODULE_NEW_ITEM_CREATE_FAIL"

// ==================== Inventory (LP) Events ====================
export const LP_SAVE_START = "LP_SAVE_START"
export const LP_SAVE_COMPLETE = "LP_SAVE_COMPLETE"
export const LP_SAVE_FAILED = "LP_SAVE_FAILED"

export const LP_COPIES_DB_DELETE_FAIL = "LP_COPIES_DB_DELETE_FAIL"
export const LP_COPIES_DB_INSERT_FAIL = "LP_COPIES_DB_INSERT_FAIL"
export const LP_COPIES_DB_UPDATE_FAIL = "LP_COPIES_DB_UPDATE_FAIL"

export const MODULE_MULTI_EDIT_START = "MODULE_MULTI_EDIT_START"
export const MODULE_MULTI_EDIT_COMPLETE = "MODULE_MULTI_EDIT_COMPLETE"
export const MODULE_MULTI_EDIT_FAIL = "MODULE_MULTI_EDIT_FAIL"
export const MODULE_MULTI_EDIT_VALIDATE_START =
  "MODULE_MULTI_EDIT_VALIDATE_START"
export const MODULE_MULTI_EDIT_VALIDATE_COMPLETE =
  "MODULE_MULTI_EDIT_VALIDATE_COMPLETE"
export const MODULE_MULTI_EDIT_VALIDATE_FAIL = "MODULE_MULTI_EDIT_VALIDATE_FAIL"

export const MODULE_BULK_ACTION_START = "MODULE_BULK_ACTION_START"
export const MODULE_BULK_ACTION_COMPLETE = "MODULE_BULK_ACTION_COMPLETE"
export const MODULE_BULK_ACTION_FAIL = "MODULE_BULK_ACTION_FAIL"
export const MODULE_BULK_DELETE_START = "MODULE_BULK_DELETE_START"
export const MODULE_BULK_DELETE_COMPLETE = "MODULE_BULK_DELETE_COMPLETE"
export const MODULE_BULK_DELETE_FAIL = "MODULE_BULK_DELETE_FAIL"
export const MODULE_BULK_UPDATE_START = "MODULE_BULK_UPDATE_START"
export const MODULE_BULK_UPDATE_COMPLETE = "MODULE_BULK_UPDATE_COMPLETE"
export const MODULE_BULK_UPDATE_FAIL = "MODULE_BULK_UPDATE_FAIL"

// ==================== Box Management Events ====================
export const BULK_MOVE_TO_BOX_START = "BULK_MOVE_TO_BOX_START"
export const BULK_MOVE_TO_BOX_SUCCESS = "BULK_MOVE_TO_BOX_SUCCESS"
export const BULK_MOVE_TO_BOX_FAIL = "BULK_MOVE_TO_BOX_FAIL"
export const ITEM_MOVE_TO_BOX_CONTEXT_APPLIED =
  "ITEM_MOVE_TO_BOX_CONTEXT_APPLIED"

// ==================== State Management Events ====================
export const STATE_INIT_START = "STATE_INIT_START"
export const STATE_INIT_COMPLETE = "STATE_INIT_COMPLETE"
export const STATE_INIT_FAIL = "STATE_INIT_FAIL"
export const STATE_MUTATION_START = "STATE_MUTATION_START"
export const STATE_MUTATION_COMPLETE = "STATE_MUTATION_COMPLETE"
export const STATE_MUTATION_FAIL = "STATE_MUTATION_FAIL"
export const STATE_ACTION_START = "STATE_ACTION_START"
export const STATE_ACTION_COMPLETE = "STATE_ACTION_COMPLETE"
export const STATE_ACTION_FAIL = "STATE_ACTION_FAIL"
export const STATE_PERSIST_START = "STATE_PERSIST_START"
export const STATE_PERSIST_COMPLETE = "STATE_PERSIST_COMPLETE"
export const STATE_PERSIST_FAIL = "STATE_PERSIST_FAIL"
export const STATE_RESTORE_START = "STATE_RESTORE_START"
export const STATE_RESTORE_COMPLETE = "STATE_RESTORE_COMPLETE"
export const STATE_RESTORE_FAIL = "STATE_RESTORE_FAIL"

// ==================== Dictionary Events ====================
export const DICT_LOAD_START = "DICT_LOAD_START"
export const DICT_LOAD_COMPLETE = "DICT_LOAD_COMPLETE"
export const DICT_LOAD_FAIL = "DICT_LOAD_FAIL"
export const DICT_CACHE_START = "DICT_CACHE_START"
export const DICT_CACHE_COMPLETE = "DICT_CACHE_COMPLETE"
export const DICT_CACHE_FAIL = "DICT_CACHE_FAIL"

// ==================== UI State Events ====================
export const UI_STATE_UPDATE_START = "UI_STATE_UPDATE_START"
export const UI_STATE_UPDATE_COMPLETE = "UI_STATE_UPDATE_COMPLETE"
export const UI_STATE_UPDATE_FAIL = "UI_STATE_UPDATE_FAIL"
export const UI_NOTIFICATION_SHOW = "UI_NOTIFICATION_SHOW"
export const UI_NOTIFICATION_HIDE = "UI_NOTIFICATION_HIDE"
export const NOTIFICATION_MOUNT_CLEAR = "NOTIFICATION_MOUNT_CLEAR"
export const NOTIFICATION_WATCH_TRIGGER = "NOTIFICATION_WATCH_TRIGGER"
export const UI_MODAL_OPEN = "UI_MODAL_OPEN"
export const UI_MODAL_CLOSE = "UI_MODAL_CLOSE"
export const UI_LOADING_START = "UI_LOADING_START"
export const UI_LOADING_COMPLETE = "UI_LOADING_COMPLETE"

// ==================== User Management Events ====================
export const USER_LOAD_START = "USER_LOAD_START"
export const USER_LOAD_COMPLETE = "USER_LOAD_COMPLETE"
export const USER_LOAD_FAIL = "USER_LOAD_FAIL"
export const USERS_LOAD_START = "USERS_LOAD_START"
export const USERS_LOAD_COMPLETE = "USERS_LOAD_COMPLETE"
export const USER_CREATE_START = "USER_CREATE_START"
export const USER_CREATE_COMPLETE = "USER_CREATE_COMPLETE"
export const USER_CREATE_FAIL = "USER_CREATE_FAIL"
export const USER_UPDATE_START = "USER_UPDATE_START"
export const USER_UPDATE_COMPLETE = "USER_UPDATE_COMPLETE"
export const USER_UPDATE_FAIL = "USER_UPDATE_FAIL"
export const USER_DELETE_START = "USER_DELETE_START"
export const USER_DELETE_COMPLETE = "USER_DELETE_COMPLETE"
export const USER_DELETE_FAIL = "USER_DELETE_FAIL"
export const USER_PERMISSION_CHECK_START = "USER_PERMISSION_CHECK_START"
export const USER_PERMISSION_CHECK_COMPLETE = "USER_PERMISSION_CHECK_COMPLETE"
export const USER_PERMISSION_CHECK_FAIL = "USER_PERMISSION_CHECK_FAIL"

// ==================== Data Transformation Events ====================
export const PARAM_TRANSFORM_START = "PARAM_TRANSFORM_START"
export const PARAM_TRANSFORM_COMPLETE = "PARAM_TRANSFORM_COMPLETE"
export const PARAM_TRANSFORM_FAIL = "PARAM_TRANSFORM_FAIL"
export const RESPONSE_NORMALIZE_START = "RESPONSE_NORMALIZE_START"
export const RESPONSE_NORMALIZE_COMPLETE = "RESPONSE_NORMALIZE_COMPLETE"
export const RESPONSE_NORMALIZE_FAIL = "RESPONSE_NORMALIZE_FAIL"

// ==================== Security Events ====================
export const SANITIZE_INPUT_START = "SANITIZE_INPUT_START"
export const SANITIZE_INPUT_COMPLETE = "SANITIZE_INPUT_COMPLETE"
export const SANITIZE_INPUT_FAIL = "SANITIZE_INPUT_FAIL"
export const INPUT_SANITIZE_START = "INPUT_SANITIZE_START"
export const INPUT_SANITIZE_COMPLETE = "INPUT_SANITIZE_COMPLETE"
export const XSS_PAYLOAD_DETECTED = "XSS_PAYLOAD_DETECTED"
export const XSS_PAYLOAD_SANITIZED = "XSS_PAYLOAD_SANITIZED"
export const SQL_INJECTION_DETECTED = "SQL_INJECTION_DETECTED"
export const SQL_INJECTION_PREVENTED = "SQL_INJECTION_PREVENTED"
export const CRYPTO_ENCRYPT_START = "CRYPTO_ENCRYPT_START"
export const CRYPTO_ENCRYPT_COMPLETE = "CRYPTO_ENCRYPT_COMPLETE"
export const CRYPTO_ENCRYPT_FAIL = "CRYPTO_ENCRYPT_FAIL"
export const CRYPTO_DECRYPT_START = "CRYPTO_DECRYPT_START"
export const CRYPTO_DECRYPT_COMPLETE = "CRYPTO_DECRYPT_COMPLETE"
export const CRYPTO_DECRYPT_FAIL = "CRYPTO_DECRYPT_FAIL"

// ==================== Validation Events ====================
export const VALIDATION_FAIL = "VALIDATION_FAIL"
export const VALIDATION_EMAIL_FAIL = "VALIDATION_EMAIL_FAIL"
export const VALIDATION_REQUIRED_FAIL = "VALIDATION_REQUIRED_FAIL"
export const VALIDATION_LENGTH_FAIL = "VALIDATION_LENGTH_FAIL"
export const VALIDATION_RANGE_FAIL = "VALIDATION_RANGE_FAIL"
export const VALIDATION_TYPE_FAIL = "VALIDATION_TYPE_FAIL"
export const VALIDATION_START = "VALIDATION_START"
export const VALIDATION_COMPLETE = "VALIDATION_COMPLETE"

// ==================== Component Lifecycle Events ====================
export const COMPONENT_MOUNT_START = "COMPONENT_MOUNT_START"
export const COMPONENT_MOUNT_COMPLETE = "COMPONENT_MOUNT_COMPLETE"
export const COMPONENT_MOUNT_FAIL = "COMPONENT_MOUNT_FAIL"
export const COMPONENT_UPDATE_START = "COMPONENT_UPDATE_START"
export const COMPONENT_UPDATE_COMPLETE = "COMPONENT_UPDATE_COMPLETE"
export const COMPONENT_UPDATE_FAIL = "COMPONENT_UPDATE_FAIL"
export const COMPONENT_UNMOUNT_START = "COMPONENT_UNMOUNT_START"
export const COMPONENT_UNMOUNT_COMPLETE = "COMPONENT_UNMOUNT_COMPLETE"

/**
 * Get all authentication events
 * @returns {Object} Authentication event IDs
 */
export function getAuthEvents() {
  return {
    AUTH_TOKEN_STORE_START,
    AUTH_TOKEN_STORE_COMPLETE,
    AUTH_TOKEN_STORE_FAIL,
    AUTH_TOKEN_VALIDATE_START,
    AUTH_TOKEN_VALIDATE_COMPLETE,
    AUTH_TOKEN_VALIDATE_FAIL,
    AUTH_TOKEN_REFRESH_START,
    AUTH_TOKEN_REFRESH_COMPLETE,
    AUTH_TOKEN_REFRESH_FAIL,
    LoginInit: AUTH_LOGIN_START,
    LoginSuccess: AUTH_LOGIN_COMPLETE,
    AUTH_LOGIN_FAIL,
    LogoutInit: AUTH_LOGOUT_START,
    LogoutSuccess: AUTH_LOGOUT_COMPLETE,
    AUTH_LOGOUT_FAIL,
    AUTH_CHECK_START,
    AUTH_CHECK_COMPLETE,
    AUTH_CHECK_FAIL,
    AUTH_STATUS_TRANSITION,
  }
}

/**
 * Get all API events
 * @returns {Object} API event IDs
 */
export function getApiEvents() {
  return {
    API_REQUEST_START,
    API_REQUEST_COMPLETE,
    API_REQUEST_FAIL,
    API_RESPONSE_START,
    API_RESPONSE_COMPLETE,
    API_RESPONSE_FAIL,
    API_INTERCEPTOR_REQUEST,
    API_INTERCEPTOR_RESPONSE,
    API_INTERCEPTOR_ERROR,
  }
}

/**
 * Get all module system events
 * @returns {Object} Module system event IDs
 */
export function getModuleEvents() {
  return {
    MODULE_ITEMS_LOAD_START,
    MODULE_ITEMS_LOAD_COMPLETE,
    MODULE_ITEMS_LOAD_FAIL,
    MODULE_ITEMS_FILTER_START,
    MODULE_ITEMS_FILTER_COMPLETE,
    MODULE_ITEMS_FILTER_FAIL,
    MODULE_ITEMS_SORT_START,
    MODULE_ITEMS_SORT_COMPLETE,
    MODULE_ITEMS_SORT_FAIL,
    MODULE_ITEMS_PAGINATE_START,
    MODULE_ITEMS_PAGINATE_COMPLETE,
    MODULE_ITEMS_PAGINATE_FAIL,
    MODULE_EDIT_START,
    MODULE_EDIT_COMPLETE,
    MODULE_EDIT_FAIL,
    MODULE_EDIT_VALIDATE_START,
    MODULE_EDIT_VALIDATE_COMPLETE,
    MODULE_EDIT_VALIDATE_FAIL,
    MODULE_EDIT_SUBMIT_START,
    MODULE_EDIT_SUBMIT_COMPLETE,
    MODULE_EDIT_SUBMIT_FAIL,
    MODULE_NEW_ITEM_CREATE_START,
    MODULE_NEW_ITEM_CREATE_COMPLETE,
    MODULE_NEW_ITEM_CREATE_FAIL,
    MODULE_MULTI_EDIT_START,
    MODULE_MULTI_EDIT_COMPLETE,
    MODULE_MULTI_EDIT_FAIL,
    MODULE_MULTI_EDIT_VALIDATE_START,
    MODULE_MULTI_EDIT_VALIDATE_COMPLETE,
    MODULE_MULTI_EDIT_VALIDATE_FAIL,
    MODULE_BULK_ACTION_START,
    MODULE_BULK_ACTION_COMPLETE,
    MODULE_BULK_ACTION_FAIL,
    MODULE_BULK_DELETE_START,
    MODULE_BULK_DELETE_COMPLETE,
    MODULE_BULK_DELETE_FAIL,
    MODULE_BULK_UPDATE_START,
    MODULE_BULK_UPDATE_COMPLETE,
    MODULE_BULK_UPDATE_FAIL,
    BULK_MOVE_TO_BOX_START,
    BULK_MOVE_TO_BOX_SUCCESS,
    BULK_MOVE_TO_BOX_FAIL,
    ITEM_MOVE_TO_BOX_CONTEXT_APPLIED,
  }
}

/**
 * Get all state management events
 * @returns {Object} State management event IDs
 */
export function getStateEvents() {
  return {
    STATE_INIT_START,
    STATE_INIT_COMPLETE,
    STATE_INIT_FAIL,
    STATE_MUTATION_START,
    STATE_MUTATION_COMPLETE,
    STATE_MUTATION_FAIL,
    STATE_ACTION_START,
    STATE_ACTION_COMPLETE,
    STATE_ACTION_FAIL,
    STATE_PERSIST_START,
    STATE_PERSIST_COMPLETE,
    STATE_PERSIST_FAIL,
    STATE_RESTORE_START,
    STATE_RESTORE_COMPLETE,
    STATE_RESTORE_FAIL,
  }
}

/**
 * Get all user management events
 * @returns {Object} User management event IDs
 */
export function getUserEvents() {
  return {
    USER_LOAD_START,
    USER_LOAD_COMPLETE,
    USER_LOAD_FAIL,
    USER_CREATE_START,
    USER_CREATE_COMPLETE,
    USER_CREATE_FAIL,
    USER_UPDATE_START,
    USER_UPDATE_COMPLETE,
    USER_UPDATE_FAIL,
    USER_DELETE_START,
    USER_DELETE_COMPLETE,
    USER_DELETE_FAIL,
    USER_PERMISSION_CHECK_START,
    USER_PERMISSION_CHECK_COMPLETE,
    USER_PERMISSION_CHECK_FAIL,
  }
}
